import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  MessageSquare, Check, X, Eye, Filter, RefreshCw, 
  Loader2, Gift, Clock, XCircle, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FEEDBACK_TYPE_LABELS = {
  bug_report: { label: 'Bug Report', icon: '🐛', color: 'bg-red-100 text-red-700' },
  feature_suggestion: { label: 'Feature', icon: '💡', color: 'bg-blue-100 text-blue-700' },
  general: { label: 'General', icon: '💬', color: 'bg-gray-100 text-gray-700' }
};

const STATUS_CONFIG = {
  pending_review: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: Gift },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle }
};

export default function FeedbackManagement({ adminToken }) {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${adminToken}` }
  };

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('feedback_type', typeFilter);
      
      const response = await axios.get(
        `${API}/api/admin/feedback?${params.toString()}`,
        axiosConfig
      );
      setFeedbackList(response.data.feedback || []);
      setStats(response.data.stats || { pending: 0, approved: 0, rejected: 0, total: 0 });
    } catch (error) {
      toast.error('Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, adminToken]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const viewDetails = async (feedbackId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/admin/feedback/${feedbackId}`,
        axiosConfig
      );
      setSelectedFeedback(response.data);
    } catch (error) {
      toast.error('Failed to load feedback details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async (feedbackId) => {
    if (!window.confirm('Approve this feedback and award 1,000 words to the user?')) return;
    
    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/feedback/${feedbackId}/approve`,
        {},
        axiosConfig
      );
      toast.success(response.data.message);
      setSelectedFeedback(null);
      fetchFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve feedback');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (feedbackId) => {
    if (!window.confirm('Reject this feedback? No reward will be given.')) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/feedback/${feedbackId}/reject`,
        {},
        axiosConfig
      );
      toast.success('Feedback rejected');
      setSelectedFeedback(null);
      fetchFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject feedback');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            <span className="text-sm text-amber-700">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-800 mt-1">{stats.pending}</p>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Approved</span>
          </div>
          <p className="text-2xl font-bold text-green-800 mt-1">{stats.approved}</p>
        </Card>
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-red-700">Rejected</span>
          </div>
          <p className="text-2xl font-bold text-red-800 mt-1">{stats.rejected}</p>
        </Card>
        <Card className="p-4 bg-gray-50 border-gray-200">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-gray-600" />
            <span className="text-sm text-gray-700">Total</span>
          </div>
          <p className="text-2xl font-bold text-gray-800 mt-1">{stats.total}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Filters:</span>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Types</option>
              <option value="bug_report">Bug Report</option>
              <option value="feature_suggestion">Feature Suggestion</option>
              <option value="general">General</option>
            </select>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchFeedback} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Feedback Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Submitted</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" />
                  </td>
                </tr>
              ) : feedbackList.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No feedback found
                  </td>
                </tr>
              ) : (
                feedbackList.map((feedback) => {
                  const typeConfig = FEEDBACK_TYPE_LABELS[feedback.feedback_type] || FEEDBACK_TYPE_LABELS.general;
                  const statusConfig = STATUS_CONFIG[feedback.status] || STATUS_CONFIG.pending_review;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <tr key={feedback.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(feedback.submitted_at || feedback.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{feedback.user_name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{feedback.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                          {feedback.user_plan || 'free'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${typeConfig.color}`}>
                          <span>{typeConfig.icon}</span>
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate">
                        {feedback.subject}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(feedback.id)}
                          className="h-8"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedFeedback.subject}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Submitted by {selectedFeedback.user_name || selectedFeedback.user_email} on{' '}
                    {new Date(selectedFeedback.submitted_at || selectedFeedback.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedFeedback(null)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Type</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                    FEEDBACK_TYPE_LABELS[selectedFeedback.feedback_type]?.color || 'bg-gray-100 text-gray-700'
                  }`}>
                    {FEEDBACK_TYPE_LABELS[selectedFeedback.feedback_type]?.icon || '💬'}
                    {FEEDBACK_TYPE_LABELS[selectedFeedback.feedback_type]?.label || selectedFeedback.feedback_type}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">User Plan</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 capitalize">
                    {selectedFeedback.user_plan || 'free'}
                  </span>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">User Balance</p>
                  <span className="font-semibold text-gray-900">
                    {selectedFeedback.user_word_balance?.toLocaleString() || '—'} words
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2 font-semibold uppercase">Description</p>
                <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedFeedback.description || selectedFeedback.message}
                </div>
              </div>

              {/* Screenshot */}
              {selectedFeedback.screenshot_url && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2 font-semibold uppercase">Screenshot</p>
                  <div className="border rounded-lg overflow-hidden">
                    <img 
                      src={selectedFeedback.screenshot_url} 
                      alt="Feedback screenshot" 
                      className="max-w-full h-auto"
                    />
                  </div>
                </div>
              )}

              {/* Status */}
              <div className="mb-6">
                <p className="text-xs text-gray-500 mb-2 font-semibold uppercase">Current Status</p>
                {(() => {
                  const statusConfig = STATUS_CONFIG[selectedFeedback.status] || STATUS_CONFIG.pending_review;
                  const StatusIcon = statusConfig.icon;
                  return (
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${statusConfig.color}`}>
                      <StatusIcon className="h-4 w-4" />
                      {statusConfig.label}
                      {selectedFeedback.reward_granted && (
                        <span className="ml-2 text-green-700">• +1,000 words awarded</span>
                      )}
                    </span>
                  );
                })()}
              </div>

              {/* Action Buttons - Only show for pending_review */}
              {selectedFeedback.status === 'pending_review' && (
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => handleReject(selectedFeedback.id)}
                    disabled={actionLoading}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApprove(selectedFeedback.id)}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Approve & Award 1,000 Words
                  </Button>
                </div>
              )}

              {/* Review Info - Show for processed feedback */}
              {selectedFeedback.status !== 'pending_review' && selectedFeedback.reviewed_at && (
                <div className="pt-4 border-t text-sm text-gray-500">
                  Reviewed by {selectedFeedback.reviewed_by || 'Admin'} on{' '}
                  {new Date(selectedFeedback.reviewed_at).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
